import React, { useState } from 'react';
import { BarChart3, TrendingUp, Package, Truck, Calendar, Info, ArrowUpRight, Sparkles } from 'lucide-react';
import { PackageItem, ShipmentOrder } from '../types';

interface MonthlyPerformanceCardProps {
  packages: PackageItem[];
  shipments: ShipmentOrder[];
  className?: string;
}

interface MonthData {
  monthKey: string; // "2026-03"
  label: string;    // "Mar"
  fullLabel: string; // "Março de 2026"
  packagesCount: number;
  shipmentsCount: number;
  totalWeightKg: number;
}

export const MonthlyPerformanceCard: React.FC<MonthlyPerformanceCardProps> = ({
  packages,
  shipments,
  className = ''
}) => {
  const [selectedMonthIndex, setSelectedMonthIndex] = useState<number | null>(null);

  // Generate last 6 months list dynamically based on current date
  const now = new Date();
  const monthsData: MonthData[] = [];

  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const yearMonth = d.toISOString().substring(0, 7); // "2026-08"
    
    // Format Month short label (e.g. "Ago")
    const shortLabel = new Intl.DateTimeFormat('pt-BR', { month: 'short' }).format(d);
    const formattedShort = (shortLabel.charAt(0).toUpperCase() + shortLabel.slice(1)).replace('.', '');

    // Format Month full label (e.g. "Agosto de 2026")
    const fullMonthName = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(d);
    const formattedFull = fullMonthName.charAt(0).toUpperCase() + fullMonthName.slice(1);

    // Count packages in this month
    const monthPackages = packages.filter((p) => {
      const dateStr = p.createdAt || p.arrivalDate;
      if (!dateStr) return false;
      return dateStr.substring(0, 7) === yearMonth;
    });

    // Count shipments in this month
    const monthShipments = shipments.filter((s) => {
      if (!s.createdAt) return false;
      return s.createdAt.substring(0, 7) === yearMonth;
    });

    const monthWeight = monthPackages.reduce((sum, p) => sum + (p.weightKg || 0), 0);

    monthsData.push({
      monthKey: yearMonth,
      label: formattedShort,
      fullLabel: formattedFull,
      packagesCount: monthPackages.length,
      shipmentsCount: monthShipments.length,
      totalWeightKg: monthWeight
    });
  }

  // Calculate totals over 6 months
  const totalPackages6m = monthsData.reduce((acc, m) => acc + m.packagesCount, 0);
  const totalShipments6m = monthsData.reduce((acc, m) => acc + m.shipmentsCount, 0);
  const totalWeight6m = monthsData.reduce((acc, m) => acc + m.totalWeightKg, 0);

  // Peak month
  const peakMonth = [...monthsData].sort((a, b) => (b.packagesCount + b.shipmentsCount) - (a.packagesCount + a.shipmentsCount))[0];

  // Max scale for chart (ensure at least 5 for nice visual proportions)
  const maxValRaw = Math.max(...monthsData.map((m) => Math.max(m.packagesCount, m.shipmentsCount)), 1);
  const maxVal = Math.max(maxValRaw, 5);

  // Selected or active month details
  const activeMonth = selectedMonthIndex !== null ? monthsData[selectedMonthIndex] : monthsData[monthsData.length - 1];

  // SVG dimensions & bar coordinates
  const svgWidth = 540;
  const svgHeight = 220;
  const paddingLeft = 35;
  const paddingRight = 20;
  const paddingTop = 25;
  const paddingBottom = 35;

  const chartAreaWidth = svgWidth - paddingLeft - paddingRight;
  const chartAreaHeight = svgHeight - paddingTop - paddingBottom;

  const groupWidth = chartAreaWidth / monthsData.length;
  const barWidth = Math.min(22, (groupWidth - 18) / 2);

  // Y-axis gridlines (0, 25%, 50%, 75%, 100%)
  const gridSteps = [0, 0.25, 0.5, 0.75, 1];

  return (
    <div className={`bg-white rounded-2xl border border-slate-200/90 shadow-sm p-5 sm:p-6 space-y-5 ${className}`}>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-[#1D3557] to-slate-700 text-white flex items-center justify-center shrink-0 shadow-xs">
            <BarChart3 className="w-5 h-5 text-amber-300" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-slate-800 tracking-tight">
                Desempenho Mensal de Suíte
              </h3>
              <span className="bg-slate-100 text-slate-600 border border-slate-200 px-2 py-0.5 rounded text-[10px] font-mono font-bold">
                Últimos 6 Meses
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Comparativo de caixas recebidas no depósito vs envios consolidados despachados.
            </p>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 text-xs font-bold bg-slate-50 p-2 rounded-xl border border-slate-200/80">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-md bg-blue-600 inline-block shadow-xs" />
            <span className="text-slate-700">Pacotes Recebidos</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-md bg-[#E63946] inline-block shadow-xs" />
            <span className="text-slate-700">Envios Processados</span>
          </div>
        </div>
      </div>

      {/* Summary Metrics Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <div className="bg-blue-50/60 border border-blue-100 rounded-xl p-3">
          <div className="flex items-center justify-between text-blue-700 font-bold mb-1">
            <span>Pacotes Chegados</span>
            <Package className="w-4 h-4 text-blue-600" />
          </div>
          <div className="text-xl font-black text-blue-900">
            {totalPackages6m} <span className="text-xs font-normal text-blue-700">unid.</span>
          </div>
          <div className="text-[10px] text-blue-600 mt-0.5 font-medium">
            Média: ~{(totalPackages6m / 6).toFixed(1)}/mês
          </div>
        </div>

        <div className="bg-red-50/60 border border-red-100 rounded-xl p-3">
          <div className="flex items-center justify-between text-red-700 font-bold mb-1">
            <span>Envios Despachados</span>
            <Truck className="w-4 h-4 text-[#E63946]" />
          </div>
          <div className="text-xl font-black text-red-950">
            {totalShipments6m} <span className="text-xs font-normal text-red-700">caixas</span>
          </div>
          <div className="text-[10px] text-red-600 mt-0.5 font-medium">
            Média: ~{(totalShipments6m / 6).toFixed(1)}/mês
          </div>
        </div>

        <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3">
          <div className="flex items-center justify-between text-slate-700 font-bold mb-1">
            <span>Peso Total Recebido</span>
            <TrendingUp className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="text-xl font-black text-slate-800">
            {totalWeight6m.toFixed(1)} <span className="text-xs font-normal text-slate-500">kg</span>
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5 font-medium">
            Acumulado no semestre
          </div>
        </div>

        <div className="bg-amber-50/60 border border-amber-200/80 rounded-xl p-3">
          <div className="flex items-center justify-between text-amber-800 font-bold mb-1">
            <span>Pico de Movimentação</span>
            <Sparkles className="w-4 h-4 text-amber-600" />
          </div>
          <div className="text-xl font-black text-amber-950">
            {peakMonth ? peakMonth.label : '-'}
          </div>
          <div className="text-[10px] text-amber-700 mt-0.5 font-medium">
            {peakMonth ? `${peakMonth.packagesCount} pacotes + ${peakMonth.shipmentsCount} envios` : 'Sem dados'}
          </div>
        </div>
      </div>

      {/* SVG Grouped Bar Chart */}
      <div className="relative pt-2">
        <svg 
          viewBox={`0 0 ${svgWidth} ${svgHeight}`} 
          className="w-full h-auto overflow-visible select-none"
        >
          {/* Definitions for Gradient Fills */}
          <defs>
            <linearGradient id="packagesGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3B82F6" />
              <stop offset="100%" stopColor="#1D4ED8" />
            </linearGradient>
            <linearGradient id="shipmentsGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#F43F5E" />
              <stop offset="100%" stopColor="#BE123C" />
            </linearGradient>
            <linearGradient id="activeHighlight" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#F1F5F9" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#E2E8F0" stopOpacity="0.2" />
            </linearGradient>
          </defs>

          {/* Y-Axis Gridlines & Scale Labels */}
          {gridSteps.map((stepRatio, idx) => {
            const yVal = Math.round(maxVal * stepRatio);
            const yPos = paddingTop + chartAreaHeight * (1 - stepRatio);
            return (
              <g key={idx}>
                <line
                  x1={paddingLeft}
                  y1={yPos}
                  x2={svgWidth - paddingRight}
                  y2={yPos}
                  stroke="#E2E8F0"
                  strokeDasharray={stepRatio === 0 ? undefined : "3 3"}
                  strokeWidth={stepRatio === 0 ? "1.5" : "1"}
                />
                <text
                  x={paddingLeft - 8}
                  y={yPos + 4}
                  textAnchor="end"
                  className="fill-slate-400 text-[10px] font-mono font-medium"
                >
                  {yVal}
                </text>
              </g>
            );
          })}

          {/* Render Bar Groups per Month */}
          {monthsData.map((m, idx) => {
            const groupX = paddingLeft + idx * groupWidth;
            const isSelected = (selectedMonthIndex === idx) || (selectedMonthIndex === null && idx === monthsData.length - 1);

            // Bar 1: Packages
            const pkgHeight = (m.packagesCount / maxVal) * chartAreaHeight;
            const pkgY = paddingTop + chartAreaHeight - pkgHeight;
            const pkgX = groupX + (groupWidth / 2) - barWidth - 2;

            // Bar 2: Shipments
            const shpHeight = (m.shipmentsCount / maxVal) * chartAreaHeight;
            const shpY = paddingTop + chartAreaHeight - shpHeight;
            const shpX = groupX + (groupWidth / 2) + 2;

            return (
              <g 
                key={m.monthKey}
                className="cursor-pointer group"
                onClick={() => setSelectedMonthIndex(idx)}
              >
                {/* Background Group Hover Highlight */}
                <rect
                  x={groupX + 4}
                  y={paddingTop - 10}
                  width={groupWidth - 8}
                  height={chartAreaHeight + 35}
                  rx="8"
                  fill={isSelected ? "url(#activeHighlight)" : "transparent"}
                  className="transition-colors group-hover:fill-slate-100/60"
                />

                {/* Package Bar */}
                {m.packagesCount > 0 ? (
                  <rect
                    x={pkgX}
                    y={pkgY}
                    width={barWidth}
                    height={Math.max(pkgHeight, 4)}
                    rx="4"
                    fill="url(#packagesGradient)"
                    className="transition-all duration-300 group-hover:opacity-90"
                  />
                ) : (
                  <rect
                    x={pkgX}
                    y={paddingTop + chartAreaHeight - 3}
                    width={barWidth}
                    height={3}
                    rx="1.5"
                    fill="#CBD5E1"
                  />
                )}

                {/* Package Count Value Label */}
                {m.packagesCount > 0 && (
                  <text
                    x={pkgX + barWidth / 2}
                    y={pkgY - 4}
                    textAnchor="middle"
                    className="fill-blue-700 text-[10px] font-bold font-mono"
                  >
                    {m.packagesCount}
                  </text>
                )}

                {/* Shipment Bar */}
                {m.shipmentsCount > 0 ? (
                  <rect
                    x={shpX}
                    y={shpY}
                    width={barWidth}
                    height={Math.max(shpHeight, 4)}
                    rx="4"
                    fill="url(#shipmentsGradient)"
                    className="transition-all duration-300 group-hover:opacity-90"
                  />
                ) : (
                  <rect
                    x={shpX}
                    y={paddingTop + chartAreaHeight - 3}
                    width={barWidth}
                    height={3}
                    rx="1.5"
                    fill="#CBD5E1"
                  />
                )}

                {/* Shipment Count Value Label */}
                {m.shipmentsCount > 0 && (
                  <text
                    x={shpX + barWidth / 2}
                    y={shpY - 4}
                    textAnchor="middle"
                    className="fill-red-700 text-[10px] font-bold font-mono"
                  >
                    {m.shipmentsCount}
                  </text>
                )}

                {/* Month Label (X-Axis) */}
                <text
                  x={groupX + groupWidth / 2}
                  y={svgHeight - 8}
                  textAnchor="middle"
                  className={`text-[11px] font-bold transition-colors ${
                    isSelected ? 'fill-[#1D3557] font-extrabold' : 'fill-slate-500 group-hover:fill-slate-800'
                  }`}
                >
                  {m.label}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Selected Month Interactive Details Bar */}
      {activeMonth && (
        <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3.5 flex flex-wrap items-center justify-between gap-3 text-xs animate-fade-in">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#1D3557]" />
            <span className="font-bold text-slate-800">
              Detalhamento de {activeMonth.fullLabel}:
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-4 font-mono font-medium text-slate-700">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-blue-600" />
              <span>Pacotes Chegados: <strong className="text-blue-900">{activeMonth.packagesCount}</strong></span>
            </div>

            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[#E63946]" />
              <span>Envios Despachados: <strong className="text-red-900">{activeMonth.shipmentsCount}</strong></span>
            </div>

            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              <span>Peso Acumulado: <strong className="text-slate-900">{activeMonth.totalWeightKg.toFixed(2)} kg</strong></span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthlyPerformanceCard;
